import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { LABELS, type DdBasis } from "@match/shared";
import { PHASE_LABEL } from "@/components/session";
import { Button, ErrorNote, Field, SidePanel, inputClass } from "@/components/ui";
import { ApiError, api, errorMessage } from "@/lib/api";
import { money, percent } from "@/lib/format";
import { useCyclesAwaitingAccount, usePropFirms, useRefreshDirectory, useServers } from "@/lib/queries";
import { decimalInput, fractionToPct, pctToFraction } from "@/lib/units";
import type { Account, BrokerServer, CycleAwaitingAccount } from "@/lib/types";

/*
 * IL PERCORSO CICLO-FASI, UNICO E GUIDATO, DALLA SCHEDA CLIENTE.
 *
 * Un solo pulsante — «Nuovo conto prop» — invece di tre porte separate
 * (`/fasi/nuova`, `/cycles/:id/next-account` chiuso dietro un curl, e niente
 * per il broker che arriva dopo). La fase la decide il menu, non l'operatore:
 * propone «Fase 1» (sempre) e, per ogni ciclo di QUESTO cliente che aspetta un
 * conto, la fase che aspetta davvero — mai le tre fisse di prima, che
 * lasciavano scegliere una combinazione che il backend avrebbe rifiutato.
 *
 * Il broker è la terza scelta indipendente: libero fra quelli del cliente,
 * nuovo, o nessuno. Senza broker la Fase nasce lo stesso, `AWAITING_BROKER`
 * — si aggancia più tardi dalla scheda della Fase (`AttachBrokerPanel`).
 */

const BASES = Object.keys(LABELS.ddBasis) as DdBasis[];

const cicloAttesaLabel = (c: CycleAwaitingAccount) =>
  `${PHASE_LABEL(c.awaitingPropPhase)} · ${c.label ?? c.propFirm.name} · ${LABELS.terms.recoverAmount} ${money(c.recoverAmount, c.currency)}`;

export function NewPropAccountPanel({
  open,
  clientId,
  clientName,
  brokerAccounts,
  /** Aperto da "Registra il conto" su un ciclo preciso: il menu parte già lì. */
  defaultTargetCycleId,
  onClose,
}: {
  open: boolean;
  clientId: string;
  clientName: string;
  /** I conti broker del cliente: da qui si sceglie quello "libero fra i miei". */
  brokerAccounts: Account[];
  defaultTargetCycleId?: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const refresh = useRefreshDirectory();
  const servers = useServers();
  const firms = usePropFirms();
  const awaiting = useCyclesAwaitingAccount({ clientId });

  // --- Il conto prop nuovo ---
  const [mt5Login, setMt5Login] = useState("");
  const [mt5Password, setMt5Password] = useState("");
  const [propServerId, setPropServerId] = useState("");
  const [propCurrency, setPropCurrency] = useState("USD");
  const [propLabel, setPropLabel] = useState("");

  // --- La fase: "PHASE_1" o l'id del ciclo che aspetta ---
  const [target, setTarget] = useState<string>(defaultTargetCycleId ?? "PHASE_1");
  const nuovaChallenge = target === "PHASE_1";
  const cicloScelto = awaiting.data?.find((c) => c.id === target) ?? null;

  // --- Solo per una challenge nuova ---
  const [presetId, setPresetId] = useState("");
  const [accountSize, setAccountSize] = useState("");
  const [fee, setFee] = useState("");
  const [maxDdPct, setMaxDdPct] = useState("");
  const [basis, setBasis] = useState<DdBasis>("STATIC_INITIAL");
  const [cycleLabel, setCycleLabel] = useState("");
  const [dailyDdPct, setDailyDdPct] = useState("");
  const [targetPct, setTargetPct] = useState("");
  const [symbol, setSymbol] = useState("");

  // --- Il broker: libero, nuovo, o nessuno ---
  const [brokerChoice, setBrokerChoice] = useState<"NONE" | "EXISTING" | "NEW">(brokerAccounts.some((a) => !a.liveSession) ? "EXISTING" : "NONE");
  const [brokerAccountId, setBrokerAccountId] = useState(() => brokerAccounts.find((a) => !a.liveSession)?.id ?? "");
  const [newBrokerLogin, setNewBrokerLogin] = useState("");
  const [newBrokerPassword, setNewBrokerPassword] = useState("");
  const [newBrokerServerId, setNewBrokerServerId] = useState("");
  const [newBrokerCurrency, setNewBrokerCurrency] = useState("USD");
  const [newBrokerLabel, setNewBrokerLabel] = useState("");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const propServers = (servers.data ?? []).filter((s) => s.kind === "PROP");
  const brokerServers = (servers.data ?? []).filter((s) => s.kind === "BROKER");
  const propServer = propServers.find((s) => s.id === propServerId);
  const newBrokerServer = brokerServers.find((s) => s.id === newBrokerServerId);
  const allPresets = (firms.data ?? []).flatMap((f) => f.presets.map((p) => ({ ...p, firmName: f.name })));
  const preset = allPresets.find((p) => p.id === presetId);
  const brokerLiberi = brokerAccounts.filter((a) => !a.liveSession);

  // Preset scelto: si precompilano taglia, fee, DD.
  useEffect(() => {
    if (!preset || !nuovaChallenge) return;
    setAccountSize(String(Number(preset.accountSize)));
    setFee(String(Number(preset.challengeFee)));
    setMaxDdPct(fractionToPct(preset.maxDdPct));
    setBasis(preset.maxDdBasis);
    setDailyDdPct(fractionToPct(preset.dailyDdPct));
    setTargetPct(fractionToPct(preset.phase1TargetPct));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetId]);

  const effective = {
    accountSize: decimalInput(accountSize),
    maxDdPct: pctToFraction(maxDdPct),
    fee: decimalInput(fee),
  };

  // Simbolo: solo per una challenge nuova. Con broker scelto (libero o nuovo con
  // server noto), l'elenco è l'intersezione fra i due server — la stessa regola
  // dell'apertura. Senza broker, quello che è mappato sul server prop: il
  // backend lo verifica di nuovo quando il broker arriva.
  const brokerServerForSymbols =
    brokerChoice === "EXISTING" ? servers.data?.find((s) => s.id === brokerAccounts.find((a) => a.id === brokerAccountId)?.serverId) : brokerChoice === "NEW" ? newBrokerServer : undefined;
  const symbolOptions = !propServer
    ? []
    : brokerServerForSymbols
      ? propServer.symbols.map((s) => s.canonical).filter((c) => brokerServerForSymbols.symbols.some((b) => b.canonical === c)).sort()
      : [...propServer.symbols.map((s) => s.canonical)].sort();

  const problems = [
    !mt5Login.trim() && "login MT5 del conto prop",
    !mt5Password && "password del conto prop",
    !propServerId && "server del conto prop",
    nuovaChallenge && !presetId && "prop firm e programma",
    nuovaChallenge && !effective.accountSize && "taglia del conto",
    nuovaChallenge && !effective.maxDdPct && "max drawdown",
    nuovaChallenge && !effective.fee && "fee",
    nuovaChallenge && !symbol && "simbolo",
    !nuovaChallenge && !cicloScelto && "quale fase continuare",
    brokerChoice === "EXISTING" && !brokerAccountId && "quale conto broker",
    brokerChoice === "NEW" && !newBrokerLogin.trim() && "login MT5 del conto broker",
    brokerChoice === "NEW" && !newBrokerPassword && "password del conto broker",
    brokerChoice === "NEW" && !newBrokerServerId && "server del conto broker",
  ].filter(Boolean) as string[];

  const submit = async () => {
    if (problems.length) return;
    setBusy(true);
    setError(null);
    setErrorCode(null);
    try {
      // Il conto prop nasce sempre: è il motivo per cui si è aperto questo pannello.
      const prop = await api.accounts.create({
        label: propLabel.trim() || `prop ${mt5Login.trim()}`,
        clientId,
        role: "PROP",
        mt5Login: mt5Login.trim(),
        mt5Password,
        serverId: propServerId,
        currency: propCurrency,
      });

      if (!nuovaChallenge && cicloScelto) {
        const body =
          brokerChoice === "EXISTING"
            ? { accountId: prop.id, brokerAccountId }
            : brokerChoice === "NEW"
              ? { accountId: prop.id, newBroker: { mt5Login: newBrokerLogin.trim(), mt5Password: newBrokerPassword, serverId: newBrokerServerId, label: newBrokerLabel.trim() || null, currency: newBrokerCurrency } }
              : { accountId: prop.id, noBroker: true as const };
        const r = await api.cycles.nextAccount(cicloScelto.id, body);
        await refresh();
        navigate(`/fasi/${r.session.id}`);
        return;
      }

      let brokerId: string | undefined;
      if (brokerChoice === "EXISTING") brokerId = brokerAccountId;
      else if (brokerChoice === "NEW") {
        const broker = await api.accounts.create({
          label: newBrokerLabel.trim() || `broker ${newBrokerLogin.trim()}`,
          clientId,
          role: "BROKER",
          mt5Login: newBrokerLogin.trim(),
          mt5Password: newBrokerPassword,
          serverId: newBrokerServerId,
          currency: newBrokerCurrency,
        });
        brokerId = broker.id;
      }

      const r = await api.sessions.create({
        clientId,
        propAccountId: prop.id,
        ...(brokerId && { brokerAccountId: brokerId }),
        canonicalSymbol: symbol,
        phase: "PHASE_1",
        presetId,
        cycleLabel: cycleLabel.trim() || null,
        accountSize: effective.accountSize!,
        challengeFee: effective.fee!,
        maxDdPct: effective.maxDdPct!,
        maxDdBasis: basis,
        dailyDdPct: dailyDdPct.trim() ? pctToFraction(dailyDdPct) : null,
        challengeTargetPct: targetPct.trim() ? pctToFraction(targetPct) : null,
      });
      await refresh();
      navigate(`/fasi/${r.session.id}`);
    } catch (e) {
      setError(errorMessage(e));
      setErrorCode(e instanceof ApiError ? e.code : null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title="Nuovo conto prop"
      subtitle={clientName}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Annulla
          </Button>
          <Button variant="primary" disabled={busy || problems.length > 0} onClick={() => void submit()} title={problems.length ? `Manca: ${problems.join(", ")}` : undefined}>
            {busy ? "Creazione…" : "Crea"}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <section className="space-y-3">
          <h3 className="text-sm font-medium text-ink">Il conto prop</h3>
          <Field label="Login MT5">{(id) => <input id={id} value={mt5Login} onChange={(e) => setMt5Login(e.target.value)} className={inputClass} />}</Field>
          <Field label="Password MT5">{(id) => <input id={id} type="password" value={mt5Password} onChange={(e) => setMt5Password(e.target.value)} className={inputClass} />}</Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Server">
              {(id) => (
                <select id={id} value={propServerId} onChange={(e) => setPropServerId(e.target.value)} className={inputClass}>
                  <option value="" disabled>
                    {propServers.length ? "Scegli il server" : "Nessun server prop"}
                  </option>
                  {propServers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.displayName ?? s.name}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label="Valuta">{(id) => <input id={id} value={propCurrency} maxLength={3} onChange={(e) => setPropCurrency(e.target.value.toUpperCase())} className={inputClass} />}</Field>
          </div>
          <Field label="Etichetta" hint="Facoltativa: se vuota, prop + il login.">
            {(id) => <input id={id} value={propLabel} onChange={(e) => setPropLabel(e.target.value)} className={inputClass} />}
          </Field>
        </section>

        <section className="space-y-3 border-t border-line pt-4">
          <h3 className="text-sm font-medium text-ink">La fase</h3>
          <Field label="Continua">
            {(id) => (
              <select id={id} value={target} onChange={(e) => setTarget(e.target.value)} className={inputClass}>
                <option value="PHASE_1">Fase 1 · nuova challenge</option>
                {(awaiting.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {cicloAttesaLabel(c)}
                  </option>
                ))}
              </select>
            )}
          </Field>

          {!nuovaChallenge && cicloScelto && (
            <dl className="grid grid-cols-2 gap-3 rounded-card border border-line bg-surface p-4 text-sm">
              <Info label="Prop firm">{cicloScelto.propFirm.name}</Info>
              <Info label="Fase che aspetta">{PHASE_LABEL(cicloScelto.awaitingPropPhase)}</Info>
              <Info label={LABELS.terms.recoverAmount}>{money(cicloScelto.recoverAmount, cicloScelto.currency)}</Info>
              <Info label="Simbolo">{cicloScelto.lastPhase?.canonicalSymbol ?? "—"}</Info>
              <p className="col-span-2 text-xs text-faint">
                Simbolo, DD e target si ricopiano dall'ultima fase: continuano la stessa coppia.
              </p>
            </dl>
          )}

          {nuovaChallenge && (
            <>
              <Field label="Prop firm e programma">
                {(id) => (
                  <select id={id} value={presetId} onChange={(e) => setPresetId(e.target.value)} className={inputClass}>
                    <option value="" disabled>
                      Scegli prop firm e modello
                    </option>
                    {(firms.data ?? []).map((f) => (
                      <optgroup key={f.id} label={f.name}>
                        {f.presets.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name} · {p.phases === 0 ? "instant" : p.phases === 1 ? "one-step" : "two-step"}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                )}
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <NumField label="Taglia del conto" value={accountSize} onChange={setAccountSize} suffix={propCurrency} />
                <NumField label="Fee pagata" value={fee} onChange={setFee} suffix={propCurrency} />
                <NumField label="Max drawdown" value={maxDdPct} onChange={setMaxDdPct} suffix="%" />
                <Field label="Base del DD">
                  {(id) => (
                    <select id={id} value={basis} onChange={(e) => setBasis(e.target.value as DdBasis)} className={inputClass}>
                      {BASES.map((b) => (
                        <option key={b} value={b}>
                          {LABELS.ddBasis[b]}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>
              </div>
              <Field label="Etichetta del ciclo" hint="Facoltativa, per riconoscerlo nello storico.">
                {(id) => <input id={id} value={cycleLabel} onChange={(e) => setCycleLabel(e.target.value)} className={inputClass} />}
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <NumField label="DD giornaliero" value={dailyDdPct} onChange={setDailyDdPct} suffix="%" hint="Vuoto se non previsto." />
                <NumField label="Target fase 1" value={targetPct} onChange={setTargetPct} suffix="%" />
              </div>
              <Field label="Simbolo" hint={!symbolOptions.length ? "Nessun simbolo mappato: mappalo dalla pagina Server, poi torna qui." : undefined}>
                {(id) => (
                  <select id={id} value={symbol} onChange={(e) => setSymbol(e.target.value)} disabled={!symbolOptions.length} className={inputClass}>
                    <option value="" disabled>
                      Scegli il simbolo
                    </option>
                    {symbolOptions.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
            </>
          )}
        </section>

        <section className="space-y-3 border-t border-line pt-4">
          <h3 className="text-sm font-medium text-ink">Il broker</h3>
          <div role="radiogroup" aria-label="Conto broker" className="grid grid-cols-3 gap-2">
            {(
              [
                ["EXISTING", "Libero fra i miei"],
                ["NEW", "Nuovo"],
                ["NONE", "Nessuno"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={brokerChoice === value}
                onClick={() => setBrokerChoice(value)}
                className={`rounded border px-2 py-1.5 text-xs ${brokerChoice === value ? "border-accent bg-raised font-medium text-ink" : "border-line text-muted hover:text-ink"}`}
              >
                {label}
              </button>
            ))}
          </div>

          {brokerChoice === "EXISTING" && (
            <Field label="Conto broker" hint={!brokerLiberi.length ? "Nessun conto broker libero: creane uno nuovo, o lascia vuoto e aggiungilo dopo." : undefined}>
              {(id) => (
                <select id={id} value={brokerAccountId} onChange={(e) => setBrokerAccountId(e.target.value)} disabled={!brokerLiberi.length} className={inputClass}>
                  <option value="" disabled>
                    {brokerLiberi.length ? "Scegli il conto" : "Nessun conto libero"}
                  </option>
                  {brokerLiberi.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.label} · {a.mt5Login}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          )}

          {brokerChoice === "NEW" && (
            <>
              <Field label="Login MT5">{(id) => <input id={id} value={newBrokerLogin} onChange={(e) => setNewBrokerLogin(e.target.value)} className={inputClass} />}</Field>
              <Field label="Password MT5">{(id) => <input id={id} type="password" value={newBrokerPassword} onChange={(e) => setNewBrokerPassword(e.target.value)} className={inputClass} />}</Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Server">
                  {(id) => (
                    <select id={id} value={newBrokerServerId} onChange={(e) => setNewBrokerServerId(e.target.value)} className={inputClass}>
                      <option value="" disabled>
                        {brokerServers.length ? "Scegli il server" : "Nessun server broker"}
                      </option>
                      {brokerServers.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.displayName ?? s.name}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>
                <Field label="Valuta">{(id) => <input id={id} value={newBrokerCurrency} maxLength={3} onChange={(e) => setNewBrokerCurrency(e.target.value.toUpperCase())} className={inputClass} />}</Field>
              </div>
              <Field label="Etichetta" hint="Facoltativa: se vuota, broker + il login.">
                {(id) => <input id={id} value={newBrokerLabel} onChange={(e) => setNewBrokerLabel(e.target.value)} className={inputClass} />}
              </Field>
            </>
          )}

          {brokerChoice === "NONE" && (
            <p className="rounded-card border border-line bg-surface p-3 text-xs text-muted">
              La fase nasce lo stesso, in attesa del conto broker: non si apre niente finché non arriva. Si aggancia più
              tardi dalla scheda della fase.
            </p>
          )}
        </section>

        <ErrorNote>{error}</ErrorNote>
        {errorCode === "BALANCE_NOT_READ" && (
          <p className="text-xs text-warn">
            Il saldo del conto broker non si è letto: il conto prop è comunque stato creato — riprova, o registralo di
            nuovo scegliendolo fra i liberi.
          </p>
        )}
      </div>
    </SidePanel>
  );
}

function NumField({ label, value, onChange, suffix, hint }: { label: string; value: string; onChange: (v: string) => void; suffix?: string; hint?: string }) {
  return (
    <Field label={label} hint={hint}>
      {(id) => (
        <div className="relative">
          <input id={id} inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)} className={`${inputClass} ${suffix ? "pr-10" : ""}`} />
          {suffix && <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-faint">{suffix}</span>}
        </div>
      )}
    </Field>
  );
}

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-faint">{label}</dt>
      <dd className="mt-0.5 text-ink">{children}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Il broker arriva dopo
// ---------------------------------------------------------------------------

/**
 * AGGANCIA IL BROKER a una Fase `AWAITING_BROKER`: libero fra i conti del
 * cliente, o nuovo. Un gesto che succede una volta sola — da qui in poi la
 * Fase si comporta come una nata con broker fin dall'inizio.
 */
export function AttachBrokerPanel({
  open,
  sessionId,
  clientId,
  clientName,
  brokerAccounts,
  onClose,
}: {
  open: boolean;
  sessionId: string;
  clientId: string;
  clientName: string;
  brokerAccounts: Account[];
  onClose: () => void;
}) {
  const refresh = useRefreshDirectory();
  const servers = useServers();
  const brokerServers = (servers.data ?? []).filter((s) => s.kind === "BROKER");
  const brokerLiberi = brokerAccounts.filter((a) => !a.liveSession);

  const [choice, setChoice] = useState<"EXISTING" | "NEW">(brokerLiberi.length ? "EXISTING" : "NEW");
  const [brokerAccountId, setBrokerAccountId] = useState(brokerLiberi[0]?.id ?? "");
  const [mt5Login, setMt5Login] = useState("");
  const [mt5Password, setMt5Password] = useState("");
  const [serverId, setServerId] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [label, setLabel] = useState("");
  const [baseAmount, setBaseAmount] = useState("");
  const [baseNote, setBaseNote] = useState("");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const problems = [
    choice === "EXISTING" && !brokerAccountId && "quale conto broker",
    choice === "NEW" && !mt5Login.trim() && "login MT5",
    choice === "NEW" && !mt5Password && "password MT5",
    choice === "NEW" && !serverId && "server",
  ].filter(Boolean) as string[];

  const submit = async () => {
    if (problems.length) return;
    const base = baseAmount.trim() ? decimalInput(baseAmount) : null;
    if (baseAmount.trim() && base === null) return setError("La base deve essere un numero, per esempio 10250.40");
    if (base !== null && baseNote.trim().length < 3) return setError("Una base indicata a mano richiede il motivo");
    setBusy(true);
    setError(null);
    setErrorCode(null);
    try {
      await api.sessions.attachBroker(sessionId, {
        ...(choice === "EXISTING" ? { accountId: brokerAccountId } : { mt5Login: mt5Login.trim(), mt5Password, serverId, label: label.trim() || null }),
        ...(base !== null ? { baseAmount, baseNote: baseNote.trim() } : {}),
      });
      await refresh();
      onClose();
    } catch (e) {
      setError(errorMessage(e));
      setErrorCode(e instanceof ApiError ? e.code : null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title="Aggiungi conto broker"
      subtitle={clientName}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Annulla
          </Button>
          <Button variant="primary" disabled={busy || problems.length > 0} onClick={() => void submit()} title={problems.length ? `Manca: ${problems.join(", ")}` : undefined}>
            {busy ? "Aggancio…" : "Aggancia"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div role="radiogroup" aria-label="Conto broker" className="grid grid-cols-2 gap-2">
          {(
            [
              ["EXISTING", "Libero fra i miei"],
              ["NEW", "Nuovo"],
            ] as const
          ).map(([value, l]) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={choice === value}
              onClick={() => setChoice(value)}
              className={`rounded border px-2 py-1.5 text-xs ${choice === value ? "border-accent bg-raised font-medium text-ink" : "border-line text-muted hover:text-ink"}`}
            >
              {l}
            </button>
          ))}
        </div>

        {choice === "EXISTING" ? (
          <Field label="Conto broker" hint={!brokerLiberi.length ? "Nessun conto libero: creane uno nuovo." : undefined}>
            {(id) => (
              <select id={id} value={brokerAccountId} onChange={(e) => setBrokerAccountId(e.target.value)} disabled={!brokerLiberi.length} className={inputClass}>
                <option value="" disabled>
                  {brokerLiberi.length ? "Scegli il conto" : "Nessun conto libero"}
                </option>
                {brokerLiberi.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label} · {a.mt5Login}
                  </option>
                ))}
              </select>
            )}
          </Field>
        ) : (
          <>
            <Field label="Login MT5">{(id) => <input id={id} value={mt5Login} onChange={(e) => setMt5Login(e.target.value)} className={inputClass} />}</Field>
            <Field label="Password MT5">{(id) => <input id={id} type="password" value={mt5Password} onChange={(e) => setMt5Password(e.target.value)} className={inputClass} />}</Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Server">
                {(id) => (
                  <select id={id} value={serverId} onChange={(e) => setServerId(e.target.value)} className={inputClass}>
                    <option value="" disabled>
                      {brokerServers.length ? "Scegli il server" : "Nessun server broker"}
                    </option>
                    {brokerServers.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.displayName ?? s.name}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
              <Field label="Valuta">{(id) => <input id={id} value={currency} maxLength={3} onChange={(e) => setCurrency(e.target.value.toUpperCase())} className={inputClass} />}</Field>
            </div>
            <Field label="Etichetta" hint="Facoltativa: se vuota, broker + il login.">
              {(id) => <input id={id} value={label} onChange={(e) => setLabel(e.target.value)} className={inputClass} />}
            </Field>
          </>
        )}

        <ErrorNote>{error}</ErrorNote>
        {errorCode === "BALANCE_NOT_READ" && (
          <div className="space-y-3 rounded-card border border-warn/40 bg-warn/5 p-3">
            <p className="text-xs text-ink">Il saldo non si è letto: dichiara la base del primo periodo, col motivo.</p>
            <Field label={`Base del primo periodo (${currency})`}>
              {(id) => <input id={id} inputMode="decimal" value={baseAmount} onChange={(e) => setBaseAmount(e.target.value)} className={inputClass} />}
            </Field>
            <Field label="Motivo della base">{(id) => <input id={id} value={baseNote} onChange={(e) => setBaseNote(e.target.value)} className={inputClass} />}</Field>
          </div>
        )}
      </div>
    </SidePanel>
  );
}
